import { acceptHotItemStore, defineItemStore } from "../itemStore.store";
import { assignDeep } from "@/helpers/utility/CollectionFunctions";

import type { StoreItem } from "../StoreItem";
import type { ChartSettings } from "@editor/items/charts/ChartItem";

export type OverviewLayoutKey = "1x1" | "2x2" | "3x2" | "2x2b" | "1x2";

type OverviewLayout<Key extends string> = {
  key: Key;
  name: string;
  tiles: number;
  templateColumns: string;
  templateRows: string;
  templateAreas?: string;
};
type OverviewLayouts = { [key in OverviewLayoutKey]: OverviewLayout<key> };

export const overviewLayouts: OverviewLayouts = {
  "1x1": {
    key: "1x1",
    name: "1x1",
    tiles: 1,
    templateColumns: "1fr",
    templateRows: "1fr",
    templateAreas: "'a1'"
  },
  "1x2": {
    key: "1x2",
    name: "1x2",
    tiles: 2,
    templateColumns: "1fr 1fr",
    templateRows: "1fr",
    templateAreas: "'a1 a2'"
  },
  "2x2b": {
    key: "2x2b",
    name: "2x2b",
    tiles: 3,
    templateColumns: "1fr 1fr",
    templateRows: "1fr 1fr",
    templateAreas: "'a1 a2' 'a1 a3'"
  },
  "2x2": {
    key: "2x2",
    name: "2x2",
    tiles: 4,
    templateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    templateRows: "1fr 1fr",
    templateAreas: "'a1 a2' 'a3 a4'"
  },
  "3x2": {
    key: "3x2",
    name: "3x2",
    tiles: 6,
    templateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
    templateRows: "1fr 1fr",
    templateAreas: "'a1 a2 a3' 'a4 a5 a6'"
  }
};

type EvaluationOverviewConfig = {
  evaluationId: number;
  index: number;

  overrideSettings: Partial<ChartSettings>;
};

export type EvaluationOverviewItem = StoreItem & {
  id: number;
  type: "evaluationOverview";
  layout: OverviewLayoutKey;
  title: string;
  tiles: (EvaluationOverviewConfig | undefined)[];
};

const itemStructure: EvaluationOverviewItem = {
  type: "evaluationOverview",
  tiles: [],

  title: "",
  layout: "2x2",

  // default properties
  id: 0,
  uid: "evaluationOverview_0",
  createdAt: Date.now()
};

export const useEvaluationOverviewStore = defineItemStore(
  "model/evaluationOverviews",
  itemStructure,
  {
    actions: {
      openEvaluation(
        evaluationOverviewId: number,
        tileIndex: number,
        evaluationId: number
      ) {
        const overview = this.items[evaluationOverviewId];
        overview.tiles[tileIndex] = {
          index: tileIndex,
          evaluationId,
          overrideSettings: {}
        };
      },

      updateTileSettings(
        evaluationOverviewId: number,
        tileIndex: number,
        settings: Partial<ChartSettings>
      ) {
        const overview = this.items[evaluationOverviewId];
        if (!overview) return;
        const tile = overview.tiles[tileIndex];
        if (!tile) return;
        assignDeep(tile.overrideSettings, settings);
      },

      removeTile(evaluationOverviewId: number, tileIndex: number) {
        const overview = this.items[evaluationOverviewId];
        if (!overview) return;
        overview.tiles[tileIndex] = undefined;
      },

      swapTiles(
        evaluationOverviewId: number,
        tileIndex1: number,
        tileIndex2: number
      ) {
        const overview = this.items[evaluationOverviewId];
        if (!overview) return;
        const tile1 = overview.tiles[tileIndex1];
        const tile2 = overview.tiles[tileIndex2];
        overview.tiles[tileIndex1] = tile2;
        overview.tiles[tileIndex2] = tile1;
        if (tile1) tile1.index = tileIndex2;
        if (tile2) tile2.index = tileIndex1;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useEvaluationOverviewStore, import.meta.hot)
  );
}
